import * as _ from "lodash";
import { ComponentProps, FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PlotEvent } from "@ant-design/charts";

import {
  Button,
  Charts,
  Col,
  DeleteOutlined,
  EditOutlined,
  Popconfirm,
  Row,
  Space,
  GamePlan as GamePlanUI,
  Typography,
  CompassOutlined,
  FileDoneOutlined
} from "@ctra/components";

import {
  DataDescriptorEntity,
  Enterprise as App,
  EnterpriseAppState,
  GamePlanEntity,
  GamePlans,
  TodoItemEntity,
  TodoItemStatus,
  TodoUpdateAction,
  ExtendedEventEntity
} from "@ctra/api";

import { Enterprise, useTranslation } from "@ctra/i18n";
import { useGoogleAnalytics } from "@ctra/analytics";
import { UnitSystem, Units, Epoch, endToEndTest } from "@ctra/utils";

import { Chart, EventList, ChartFilterContext, ChartAPIContext, useUnit } from "@chart";
import { useTargetAnnotation, GAActions } from "@game-plans";
import { FarmTag } from "@farms";
import { EventListContext } from "@events";

import { TodoListProvider } from "../../providers";
import { useTaskUpdateCycle } from "../../hooks";
import { TaskEntryExtras } from "../TaskEntryExtras";
import style from "./GamePlan.module.less";
import { useLocalization } from "@base";
import { useScorecard, ScorecardContext } from "@scorecard";

const { Text } = Typography;
const { ChartVariant } = Charts;

const {
  V1: { DateRange, TaskList: TaskListUI, TaskVariant, Timestamp },
  V2: {
    List: { KPI, NumericValue, KPIOverview }
  }
} = GamePlanUI;

type GamePlanProps = GamePlanEntity & {
  /**
   * delete game plan
   */
  onDelete: () => void;
  /**
   * edit game plan
   */
  onEdit: () => void;
};

/**
 * Complete game plan
 * @param props
 * @constructor
 */
export const GamePlan: FC<GamePlanProps> = ({
  onDelete,
  onEdit,
  id,
  dataDescriptorID,
  farmID,
  description,
  timePeriod,
  goal: { value },
  unitSystem,
  title
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { updatingTasks, setUpdatingTask } = useTaskUpdateCycle();
  const { unitSystem: globalUnitSystem } = useLocalization();
  const [eventList, setEventList] = useState<ExtendedEventEntity[]>([]);

  const {
    meta: { isLoading },
    value: scoreCardValue
  } = useScorecard();

  const {
    gamePlan: {
      labels,
      actions,
      todo: { lastModified },
      list: { headers }
    },
    kpi,
    unitSystem: { unit },
    overview: {
      panel: {
        gamePlan: { itemHeader }
      }
    }
  } = Enterprise;

  const {
    dataProperties: { typeName },
    valueProperties: { units, acquisitionMethod }
  } = useSelector<EnterpriseAppState, DataDescriptorEntity>((state) =>
    App.entities.getDataDescriptor(state, { id: dataDescriptorID })
  );

  const currentUnit = useUnit(dataDescriptorID);

  /**
   * Convert the value to the right system when necessary
   */
  const convertedValue = value
    ? Units.convertToUnitSystem(value, units, {
        from: unitSystem as UnitSystem,
        to: globalUnitSystem
      })
    : value;

  /**
   * Make annotations for the target
   * @type {Annotation[] | undefined}
   */
  const targetAnnotations = useTargetAnnotation(convertedValue, t<string>(labels.goal), dataDescriptorID);

  /**
   * Render extra UI for tasks
   * @param task
   */
  const renderExtras = (task: TodoItemEntity) =>
    task.assignedTo ? <TaskEntryExtras withUser task={task} /> : null;

  /**
   * Render a timestamp editor when we have a lst modified epoch
   * @param task
   * @param loading
   */
  const renderTimestamp: ComponentProps<typeof TaskListUI>["renderTimestamp"] = (task, loading) => {
    const { modifiedEpoch, status } = task;

    return status === TodoItemStatus.Completed && modifiedEpoch ? (
      <Timestamp
        label={t<string>(lastModified, { status: status })}
        dateTime={modifiedEpoch}
        loading={loading}
        onChange={(value) =>
          dispatch(
            GamePlans.actions.updateTask.start(
              id, // game plan id
              { ...task, modifiedEpoch: Number(value) },
              TodoUpdateAction.update
            )
          )
        }
      />
    ) : null;
  };

  /**
   * header with game plan title, date rang, farm name and edit/delete buttons
   */
  const header = (
    <Row gutter={[0, 8]} className={style.Section}>
      <Col span={24}>
        <Row justify="space-between" align="middle">
          <Text type="secondary">
            <CompassOutlined /> {t(itemHeader)}
          </Text>
          <Space direction="horizontal" className={style.Actions}>
            <Button
              type="secondary"
              size="middle"
              icon={<EditOutlined />}
              data-testid="edit-button"
              onClick={() => {
                window.scrollTo(0, 0);
                onEdit();
              }}
            />
            <Popconfirm
              overlayClassName={style.PopConfirm}
              title={t<string>(actions.delete.confirm.title)}
              okText={t<string>(actions.delete.confirm.yes)}
              cancelText={t<string>(actions.delete.confirm.no)}
              onConfirm={onDelete}
            >
              <Button type="secondary" size="middle" icon={<DeleteOutlined />} data-testid="delete-button" />
            </Popconfirm>
          </Space>
        </Row>
      </Col>
      <Col span={24}>
        <Text className={style.IncreaseFontSize}>{title}</Text>
      </Col>
      <Col span={24}>
        <FarmTag id={farmID} />
      </Col>
      <Col span={24}>
        <DateRange {...timePeriod} />
      </Col>
    </Row>
  );

  /**
   * Chart with kpi information
   */
  const chart = (
    <Row className={style.Section}>
      <ChartFilterContext.Provider>
        <EventListContext.Provider farmID={farmID}>
          <Col span={24} className={style.KPI}>
            <Text type="secondary">{t<string>(labels.kpi)}</Text>
            <KPI
              name={t<string>(kpi.displayName(typeName), {
                case: null,
                variant: null,
                makeDefaultValue: true
              })}
            />
          </Col>
          <ChartAPIContext.Provider dataDescriptorID={dataDescriptorID} farmID={farmID}>
            <Chart
              viewOptions={{ showAnnotations: true, showToolbar: true, showEventToggle: true }}
              config={{
                annotations: targetAnnotations,
                onReady: (plot) => {
                  plot.on("element:click", (event: PlotEvent) => {
                    const shape = _.get(event, ["data", "shape"]);
                    const eventList = _.get(event, ["data", "data", "events"]);

                    if (shape === "farm-event-point") {
                      setEventList((prev) => (_.isEqual(prev, eventList) ? [] : eventList));
                    }
                  });
                }
              }}
              variant={ChartVariant.Embedded}
            />
            <Col span={24}>{_.isEmpty(eventList) ? null : <EventList data={eventList} />}</Col>
          </ChartAPIContext.Provider>
        </EventListContext.Provider>
      </ChartFilterContext.Provider>
    </Row>
  );

  const pastValue = (
    <ScorecardContext.Provider
      dataDescriptorID={dataDescriptorID}
      farmID={+farmID}
      date={Epoch.asMoment(timePeriod.startEpoch).toISOString()}
    >
      <ScorecardContext.Consumer>
        {({ value: pastValue, meta: { isLoading: isPastValueLoading } }) => (
          <NumericValue
            isLoading={isPastValueLoading}
            value={pastValue}
            unit={t<string>(unit(currentUnit))}
          />
        )}
      </ScorecardContext.Consumer>
    </ScorecardContext.Provider>
  );

  return (
    <div data-testid={endToEndTest("gamePlan", "entity")}>
      {header}
      <Row align="top" className={style.Section}>
        <KPIOverview
          headers={{
            title: t(headers.kpi),
            current: t(headers.current),
            start: t(headers.start),
            target: t(headers.target)
          }}
          KPIType={t<string>(kpi.displayName(typeName), {
            case: null,
            variant: null,
            makeDefaultValue: true
          })}
          acquisitionMethod={t<string>(kpi.acquisitionMethod(acquisitionMethod))}
          current={{
            isLoading,
            scoreCardValue,
            unit: t<string>(unit(currentUnit))
          }}
          targetValue={convertedValue}
          past={pastValue}
        />
      </Row>
      {!!description && (
        <Row className={style.Section}>
          <Col>
            <Row gutter={[0, 10]}>
              <Col span={24}>
                <Text strong type="secondary">
                  {t<string>(labels.description)}
                </Text>
              </Col>
              <Col span={24}>
                <Text>{description}</Text>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <Row align="middle" className={style.Todo}>
        <Col>
          <FileDoneOutlined />
        </Col>
        <Col>
          <Text strong type="secondary">
            {t<string>(labels.todo)}
          </Text>
        </Col>
      </Row>
      <Row className={style.Tasks}>
        <Col span={24}>
          <TodoListProvider gamePlanID={id}>
            {(todoList) => (
              <TaskListUI
                items={_.values(todoList)}
                variant={TaskVariant.Expanded}
                loading={updatingTasks}
                onChange={(task, action) => {
                  /* istanbul ignore next */
                  trackEvent(task.status ? GAActions.unmarkTask : GAActions.markTask);
                  dispatch(GamePlans.actions.updateTask.start(id, task, action));
                  setUpdatingTask(task);
                }}
                extra={renderExtras}
                renderTimestamp={renderTimestamp}
              />
            )}
          </TodoListProvider>
        </Col>
      </Row>
      {chart}
    </div>
  );
};
